import { parseDateString } from '../../../../utils/custom/parseDateString';
import styles from "../../../../styles/pages/Lead/LeadManagement/Comments.module.css";
import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useParams } from "react-router-dom";
import { getCommentsApi } from "../../../../utils/api/Lead/getCommentsApi";
import { addCommentApi } from "../../../../utils/api/Lead/Comments/addCommentApi";
import { deleteCommentApi } from "../../../../utils/api/Lead/Comments/deleteCommentApi";
import { LoadingCircle } from "../../../../components/UI/Custom/LoadingCircle";
import CommentsDisabledIcon from '@mui/icons-material/CommentsDisabled';
import { handleError } from '../../../../utils/custom/errorHandler';
import { Notification } from '../../../../components/UI/Notifications';
import { LangContext } from '../../../../context/LangContext';
import { ThemeContext } from '../../../../context/ThemeContext';
import { ConfirmationModal } from '../../../../components/UI/Custom/ConfirmationModal';
import DeleteIcon from '@mui/icons-material/Delete';

const translations = {
    ru: {
        addCommentPlaceholder: "Напишите комментарий...",
        addButton: "Добавить",
        addingButton: "Добавление...",
        deleteButton: "Удалить",
        deletingButton: "Удаление...",
        addCommentError: "Пожалуйста, введите текст комментария.",
        noComments: "Нет комментариев.",
        previousPage: "Назад",
        nextPage: "Вперед",
        confirmTitle: "Подтвердите удаление комментария",
        confirmMessage: "Вы уверены, что хотите безвозвратно удалить этот комментарий?",
        confirmText: "Да, удалить",
        cancelText: "Отмена",
        deleteSuccess: "Комментарий успешно удален.",
    },
    en: {
        addCommentPlaceholder: "Write a comment...",
        addButton: "Add",
        addingButton: "Adding...",
        deleteButton: "Delete",
        deletingButton: "Deleting...",
        addCommentError: "Please enter a comment.",
        noComments: "No comments.",
        previousPage: "Previous",
        nextPage: "Next",
        confirmTitle: "Confirm Comment Deletion",
        confirmMessage: "Are you sure you want to permanently delete this comment?",
        confirmText: "Yes, delete",
        cancelText: "Cancel",
        deleteSuccess: "Comment successfully deleted.",
    },
};

export const Comments = ({ role }) => {
    const { langSelected } = useContext(LangContext);
    const { theme } = useContext(ThemeContext);
    const currentTranslation = translations[langSelected] || translations.ru;

    const [comments, setComments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const { id } = useParams();
    const [newCommentText, setNewCommentText] = useState('');
    const [addingComment, setAddingComment] = useState(false);
    
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [commentIdToDelete, setCommentIdToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);


    const loadComments = useCallback(async () => {
        setLoading(true);
        setErrors({});
        try {
            const { data, meta } = await getCommentsApi(id, role, currentPage, perPage);
            setTotalPages(meta.total_pages);
            setComments(data);
        } catch (error) {
            handleError(error.response?.data || error, setErrors);
            setComments([]);
        } finally {
            setLoading(false);
        }
    }, [id, role, currentPage, perPage]);

    useEffect(() => {
        loadComments();
    }, [loadComments]);

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    const handleAddComment = async () => {
        if (!newCommentText.trim()) {
            setErrors(prev => ({ ...prev, addComment: currentTranslation.addCommentError }));
            return;
        }
        setAddingComment(true);
        setErrors(prev => ({ ...prev, addComment: null }));
        try {
            await addCommentApi(id, newCommentText, role);
            setNewCommentText('');
            setCurrentPage(1);
            await loadComments();
        } catch (error) {
            handleError(error.response?.data || error, setErrors, 'addComment');
        } finally {
            setAddingComment(false);
        }
    };

    const handleDeleteClick = (commentId) => {
        setErrors({});
        setCommentIdToDelete(commentId);
        setIsConfirmOpen(true);
    };

    const handleCloseConfirmModal = () => {
        setIsConfirmOpen(false);
        setCommentIdToDelete(null);
    };

    const handleConfirmDelete = async () => {
        if (!commentIdToDelete) return;
        
        setIsDeleting(true);
        setErrors({});

        try {
            await deleteCommentApi(id, commentIdToDelete, role);
            
            await loadComments();
            
        } catch (error) {
            handleError(error.response?.data || error, setErrors, 'deleteComment');
        } finally {
            setIsDeleting(false);
            handleCloseConfirmModal();
        }
    };

    const renderPaginationButtons = () => {
        const pages = [];
        const maxPageButtons = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxPageButtons / 2));
        let endPage = Math.min(totalPages, startPage + maxPageButtons - 1);

        if (endPage - startPage + 1 < maxPageButtons) {
            startPage = Math.max(1, endPage - maxPageButtons + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(
                <button
                    key={i}
                    className={`${styles.paginationButton} ${i === currentPage ? styles.active : ''} ${theme === 'dark' ? styles.darkPaginationButton : ''} ${i === currentPage ? styles.darkActivePaginationButton : ''}`}
                    onClick={() => handlePageChange(i)}
                    disabled={loading}
                >
                    {i}
                </button>
            );
        }
        return pages;
    };

    if (loading && comments.length === 0) {
        return <LoadingCircle size={30} thickness={1} />;
    }

    const addCommentContainerClass = `${styles.addCommentContainer} ${theme === 'dark' ? styles.darkAddCommentContainer : ''}`;
    const commentInputClass = `${styles.commentInput} ${theme === 'dark' ? styles.darkCommentInput : ''}`;
    const noCommentsClass = `${styles.noComments} ${theme === 'dark' ? styles.darkNoComments : ''}`;
    const noCommentsIconClass = `${styles.noCommentsIcon} ${theme === 'dark' ? styles.darkNoCommentsIcon : ''}`;
    const noCommentsTextClass = `${styles.noCommentsText} ${theme === 'dark' ? styles.darkNoCommentsText : ''}`;
    const paginationClass = `${styles.pagination} ${theme === 'dark' ? styles.darkPagination : ''}`;


    return (
        <div className={styles.comments}>
            {errors.general && <Notification message={errors.general} type={"error"} />}
            <div className={addCommentContainerClass}>
                <textarea
                    className={commentInputClass}
                    value={newCommentText}
                    onChange={(e) => setNewCommentText(e.target.value)}
                    placeholder={currentTranslation.addCommentPlaceholder}
                />
                <button onClick={handleAddComment} className={`${styles.addButton} ${theme === 'dark' ? styles.darkAddButton : ''}`} disabled={addingComment}>
                    {addingComment ? currentTranslation.addingButton : currentTranslation.addButton}
                </button>
                {errors.addComment && <div className={styles.error}>{errors.addComment}</div>}
            </div>

            {comments.length > 0 ? (
                <ul className={styles.commentsList}>
                    {comments.map(comment => {

                        const parsedDate = parseDateString(comment.created_at);
                        const dateString = parsedDate ? parsedDate.toLocaleString() : 'Invalid Date';

                        return (
                            <li key={comment.id} className={`${styles.commentItem} ${theme === 'dark' ? styles.darkCommentItem : ''}`}>
                                <div className={`${styles.commentHeader} ${theme === 'dark' ? styles.darkCommentHeader : ''}`}>
                                    <div className={styles.authorDate}>
                                        <div className={`${styles.authorBlock} ${theme === 'dark' ? styles.darkAuthorBlock : ''}`}>
                                            <span>{comment.created_by?.first_name} {comment.created_by?.last_name}</span>
                                        </div>
                                        <div className={`${styles.dateBlock} ${theme === 'dark' ? styles.darkDateBlock : ''}`}>
                                            <span className={styles.createdAt}>
                                                {dateString}
                                            </span>
                                        </div>
                                    </div>
                                    
                                    <button
                                        onClick={() => handleDeleteClick(comment.id)}
                                        className={`${styles.deleteButton} ${theme === 'dark' ? styles.darkDeleteButton : ''}`}
                                        disabled={isDeleting}
                                    >
                                        {isDeleting ? (
                                            <LoadingCircle size={14} thickness={2} color="white" />
                                        ) : (
                                            <DeleteIcon style={{ fontSize: '1.1rem' }} />
                                        )}
                                    </button>
                                    
                                    {errors.deleteComment && commentIdToDelete === comment.id && (
                                        <div className={styles.error}>{errors.deleteComment}</div>
                                    )}
                                </div>
                                <p className={`${styles.commentText} ${theme === 'dark' ? styles.darkCommentText : ''}`}>{comment.comment}</p>
                            </li>
                        );
                    })}
                </ul>
            ) : (
                <div className={noCommentsClass}>
                    <CommentsDisabledIcon className={noCommentsIconClass} />
                    <p className={noCommentsTextClass}>{currentTranslation.noComments}</p>
                </div>
            )}

            {totalPages > 1 && (
                <div className={paginationClass}>
                    <button
                        type="button"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1 || loading}
                        className={`${styles.paginationButton} ${theme === 'dark' ? styles.darkPaginationButton : ''}`}
                    >
                        {currentTranslation.previousPage}
                    </button>
                    {renderPaginationButtons()}
                    <button
                        type="button"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages || loading}
                        className={`${styles.paginationButton} ${theme === 'dark' ? styles.darkPaginationButton : ''}`}
                    >
                        {currentTranslation.nextPage}
                    </button>
                </div>
            )}
            
            <ConfirmationModal
                isOpen={isConfirmOpen}
                onClose={handleCloseConfirmModal}
                onConfirm={handleConfirmDelete}
                title={currentTranslation.confirmTitle}
                message={currentTranslation.confirmMessage}
                confirmText={isDeleting ? currentTranslation.deleting : currentTranslation.confirmText}
                cancelText={currentTranslation.cancelText}
            />
        </div>
    );
};